'use client'

import classNames from 'classnames'
import { useTranslation } from 'next-i18next'
import { useMemo, useRef, useState } from 'react'

import { SvdProductCard } from '@src/components/svd-product-card'
import { openSvdQuickAdd } from '@src/components/svd-product-card/add-to-bag/svd-quick-add'
import { getInitialSvdVariant, getSvdCardListKey } from '@src/components/svd-product-card/svd-product-card.helpers'
import { getSvdApplicableTags } from '@src/components/svd-product-card/svd-product-card.promo'
import { useQuinceRouter as useRouter } from '@src/modules/navigation'
import { Button, BUTTON_SIZE, BUTTON_TYPES } from '@src/quince-components/src'
import { SmartLoaderWrapper } from '@src/quince-components/src/atoms/loaderWrapper'
import { ProductCTAConstants } from '@src/quince-components/src/atoms/productCTA/constants'
import type { TrackConfig } from '@src/quince-components/src/hocs/withTracking'
import CUSTOM_SECTION_CONTAINER_NAMES from '@src/quince-site-common/src/constants/customSectionContainerNames'
import CUSTOM_SECTION_CONTAINER_TYPES from '@src/quince-site-common/src/constants/customSectionContainerTypes'
import useATCVsATBExperiment from '@src/quince-site-common/src/hooks/use-atc-vs-atb-experiment'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { useAppInstallAccountsPresenter } from '@src/quince-site-common/src/organisms/appLayout/molecules/footer/molecules/app-install/app-install.presenter'
import getMaxWidthInPx from '@src/quince-site-common/src/utils/getMaxWidthInPx'
import { FINDING_METHOD_AGGREGATE, setFindingMethod } from '@src/telemetry/findingMethod'
import type { SvdNativeCardVariant } from '@src/types/svd-product-card.types'

import styles from '../wishlist-components/wishlist-item-list/wishlist-item-list.module.scss'

import { SvdWishlistCard } from './svd-wishlist.types'

const WISHLIST_DISPLAY_NAME = 'Wishlist'
const WISHLIST_TRACKER = 'wishlist'
const TRACK_CATEGORY = 'product-list'

/**
 * SVD wishlist grid, **legacy-SCSS layout** — the non-DS half of the pair; `wishlist-item-list-svd.tsx`
 * picks between this and `wishlist-item-list-svd-ds.tsx` on `shouldUseDesignSystem()`.
 *
 * The SCSS grid here sizes its columns in `rem` for the SCSS card body and styles the tile via
 * `.productCard` (`product-card-style` mixin), which is why it must NOT be used on a DS page: there
 * `SvdProductCard` renders its DS body, which ignores `maxWidth`/`className` and expects the grid to
 * size it — the STRFRNT-16908 misalignment.
 *
 * Renders the native `SvdProductCard` for each hydrated item, pre-selecting the wishlisted variant. The card's own heart handles removal (guest = localStorage). A desktop-only
 * "add to bag" button-underneath opens quick-add via the shared `openSvdQuickAdd` (Path A: the
 * existing enrich modal) — the card's hover bag icon is suppressed here (`hideBuyNow`), mirroring the
 * DS/legacy wishlist where the button-underneath is the add-to-cart affordance. Reached only under
 * `shouldUseSvdCard()` (branched at the page), so the legacy / DS lists are untouched.
 */
function WishlistItemListSvdLegacy({
  items,
  isLoading = false,
  sectionConfig: { tracker },
  isSelfWishlist = false,
}: {
  items: SvdWishlistCard[] | undefined
  isLoading?: boolean
  sectionConfig: { tracker?: string }
  isSelfWishlist?: boolean
}) {
  const deviceInfo = useDeviceInfo()
  const { t: tCommon } = useTranslation('common')
  // STRFRNT-17429: the non-SVD list (`wishlist-item-list-legacy.tsx`) drops the grid's default
  // `12.2rem` top margin to `3.2rem` (`wishListWrapperCompact`) whenever the app-install banner is
  // showing above it — the banner already occupies that vertical space, so the full margin on top of
  // it reads as a large empty gap. This list shares the same heading (`WishlistHeadingLegacy`, driven
  // by the same `shouldRenderAppInstallAccounts`) but never applied the compact class itself, so SVD
  // wishlist pages showed the gap unconditionally while control correctly collapsed it.
  const { shouldRender: shouldRenderAppInstallAccounts } = useAppInstallAccountsPresenter({ placement: 'wishlist' })
  const showATB = useATCVsATBExperiment()
  const atcLabel = showATB ? ProductCTAConstants.ADD_TO_BAG : ProductCTAConstants.ADD_TO_CART
  // Per-item selected variant, driven by the card's `onVariantChange` (swatch pick on the wishlist tile).
  const [selectedVariants, setSelectedVariants] = useState<Record<string, SvdNativeCardVariant>>({})

  const maxWidth = getMaxWidthInPx({
    maxWidthMobileInRem: styles.productCardWidthMobile ?? '',
    maxWidthDesktopInRem: styles.productCardWidth ?? '',
    deviceType: deviceInfo.deviceType,
  })

  // Newest first, matching the legacy list.
  const reversedItems = useMemo(() => (items ? [...items].reverse() : []), [items])

  // Bumps whenever `items` is replaced by a fresh fetch (every successful fetch is a brand-new array —
  // `useFetch` always calls `setResponse(freshArray)`, never appends). Folded into each tile's REACT
  // key (not `uniqueKey` itself, which stays stable for the selectedVariants lookup below) so a fresh
  // fetch forces every tile to remount.
  //
  // Why this is needed: `SvdProductCard`'s local swatch pick (`userVariantId` in
  // `use-svd-product-card.ts`) only resets when ITS OWN `selectedVariantId` prop changes. Preview a
  // DIFFERENT color on an existing tile, then heart that color — a NEW sibling entry is created for
  // it (no server-side duplicate), but the ORIGINAL tile's own `selectedVariantId` never changed, and
  // its stale swatch pick is STILL a valid member of `cardVariants` (just another color of the SAME
  // product), so the hook's own "self-heals when the picked id is no longer a member" premise never
  // fires. The original tile keeps showing the previewed color forever, visually matching its new
  // sibling — looking exactly like a duplicated wishlist entry when no such duplicate ever existed
  // (STRFRNT-17133, third occurrence). Forcing a remount on every wishlist refetch is the fix: it
  // can't leave a stale local pick behind because there's no old fiber to leave it on.
  //
  // TODO(STRFRNT-17133 follow-up): this is a blunt instrument — it resets every tile's local state on
  // every refetch, not just the one tile that actually needs it, because "a sibling was just created"
  // is a fact about the LIST, not about any individual card's own props. The real fix is making
  // `SvdProductCard` support a genuinely controlled `selectedVariant`/`onVariantChange` pair (opt-in,
  // so the ~20 other render sites relying on today's pseudo-controlled/persist-across-re-renders
  // behavior are untouched) and having this list feed its own `selectedVariants` state (already
  // tracked below for the "Add to Bag" button) straight into the card instead of letting the card own
  // an internal pick that can go stale. That eliminates the whole class of bug instead of papering
  // over one instance of it.
  const itemsRef = useRef(items)
  const generationRef = useRef(0)
  if (itemsRef.current !== items) {
    itemsRef.current = items
    generationRef.current += 1
  }

  return (
    <SmartLoaderWrapper isLoading={isLoading} hasData={reversedItems.length > 0} minHeight="100vh">
      <div className={styles.wishListContainer}>
        {reversedItems.length > 0 ? (
          <div
            className={classNames(styles.wishListWrapper, {
              [styles.wishListWrapperCompact as string]: shouldRenderAppInstallAccounts,
            })}
          >
            {reversedItems.map(({ product, selectedVariantId }) => {
              // Same key every collection/PLP SVD grid uses (`getSvdCardListKey`), and for the same
              // reason: the SAME productId can occupy two sibling tiles here (a product wishlisted in
              // two colors), so the key must be per-*card* (productId + the wishlisted variant's own
              // selectedAttributes), not per-variantId — a raw `variantId` isn't guaranteed to vary 1:1
              // with the wishlisted color, and a collision here made two tiles for the same product
              // share React identity, letting one tile's swatch-selection state leak onto the other
              // (STRFRNT-17133).
              const uniqueKey = getSvdCardListKey(product)
              const initialVariant = getInitialSvdVariant(product.cardVariants, selectedVariantId)
              const trackConfig: TrackConfig = {
                trackInternal: true,
                trackGA: true,
                // @i18n-ignore
                category: TRACK_CATEGORY,
                action: `${tracker ?? WISHLIST_TRACKER}__click_product_wishlist`,
                value: product.relatedIds.spid.toString(),
                context: {
                  contentContainerType: CUSTOM_SECTION_CONTAINER_TYPES.PRODUCT_CARD,
                  contentContainerName: CUSTOM_SECTION_CONTAINER_NAMES.SUBCOLLECTION_DISPLAY,
                  // @i18n-ignore
                  contentContainerDisplayName: WISHLIST_DISPLAY_NAME,
                  findingMethod: FINDING_METHOD_AGGREGATE.WISHLIST,
                },
              }

              return (
                <div
                  key={`${uniqueKey}_${generationRef.current}`}
                  className={styles.wishListItemWrapper}
                  onClick={() => {
                    setFindingMethod[FINDING_METHOD_AGGREGATE.WISHLIST]?.()
                  }}
                >
                  <SvdProductCard
                    product={product}
                    selectedVariantId={selectedVariantId}
                    maxWidth={maxWidth}
                    className={classNames(styles.productCard)}
                    hideBuyNow
                    showDeleteBtn={isSelfWishlist}
                    hideDeleteForOtherColors
                    productClickTrackConfig={trackConfig}
                    onVariantChange={variant => {
                      setSelectedVariants(prev => ({
                        ...prev,
                        [uniqueKey]: variant,
                      }))
                    }}
                  />
                  {deviceInfo.deviceType === DEVICE_TYPES.DESKTOP ? (
                    <div className={styles.addToCartBtnContainer}>
                      <Button
                        type={BUTTON_TYPES.OUTLINED}
                        onClick={() => {
                          const currentVariant = selectedVariants[uniqueKey] ?? initialVariant
                          openSvdQuickAdd({
                            product,
                            selectedVariant: currentVariant,
                            applicableTags: getSvdApplicableTags(currentVariant),
                            trackConfig,
                          })
                        }}
                        className={styles.addToCartBtn}
                      >
                        {(selectedVariants[uniqueKey] ?? initialVariant)?.appExclusive
                          ? tCommon('shop_in_the_app').toUpperCase()
                          : tCommon(atcLabel)}
                      </Button>
                    </div>
                  ) : null}
                </div>
              )
            })}
          </div>
        ) : (
          <EmptyWishList />
        )}
      </div>
    </SmartLoaderWrapper>
  )
}

function EmptyWishList() {
  const router = useRouter()
  const { t } = useTranslation('wishlist')
  return (
    <div className={styles.emptyWishlist}>
      <h2 className={styles.emptyWishlist__title}>{t('your_wishlist_is_empty')}</h2>
      <Button
        onClick={() => {
          router.push('/women')
        }}
        round={true}
        size={BUTTON_SIZE.MEDIUM}
        type={BUTTON_TYPES.SECONDARY}
        className={styles.emptyWishlist__cta}
      >
        {t('shop_now')}
      </Button>
    </div>
  )
}

export default WishlistItemListSvdLegacy
